import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useNavigate } from "react-router-dom";

interface OpportunityData {
  month: number;
  "Close Won": string;
  "Close Lost": string;
  close_won_amount: string;
  close_lost_amount: string;
}

export default function OpportunityCloseChart() {
  const [series, setSeries] = useState([
    { name: "Opportunities Won", data: new Array(12).fill(0) },
    { name: "Opportunities Lost", data: new Array(12).fill(0) },
  ]);
  const [amounts, setAmounts] = useState<{
    won: number[];
    lost: number[];
  }>({ won: new Array(12).fill(0), lost: new Array(12).fill(0) });
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  useEffect(() => {
    const fetchData = async () => {
      const token = sessionStorage.getItem("access_token");
      if (!token) {
        setError("Authorization required. Please log in.");
        navigate("/signin");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/opportunity-close-status`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }

        const result = await response.json();

        if (result.status === 200 && result.data) {
          const wonData = new Array(12).fill(0);
          const lostData = new Array(12).fill(0);
          const wonAmounts = new Array(12).fill(0);
          const lostAmounts = new Array(12).fill(0);

          result.data.forEach((item: OpportunityData) => {
            const monthIndex = item.month - 1;
            if (monthIndex >= 0 && monthIndex < 12) {
              wonData[monthIndex] = parseInt(item["Close Won"]) || 0;
              lostData[monthIndex] = parseInt(item["Close Lost"]) || 0;
              wonAmounts[monthIndex] = parseFloat(item.close_won_amount) || 0;
              lostAmounts[monthIndex] = parseFloat(item.close_lost_amount) || 0;
            }
          });

          setSeries([
            { name: "Opportunities Won", data: wonData },
            { name: "Opportunities Lost", data: lostData },
          ]);
          setAmounts({ won: wonAmounts, lost: lostAmounts });
        } else {
          setError("Invalid data format received from API");
        }
      } catch (err) {
        setError("Error fetching opportunity data");
        console.error(err);
      }
    };

    fetchData();
  }, [navigate]);

  const options: ApexOptions = {
    legend: {
      show: false,
      position: "top",
      horizontalAlign: "left",
    },
    colors: ["#22C55E", "#EF4444"],
    chart: {
      fontFamily: "Outfit, sans-serif",
      height: 310,
      type: "line",
      toolbar: {
        show: false,
      },
    },
    stroke: {
      curve: "straight",
      width: [2, 2],
    },
    fill: {
      type: "gradient",
      gradient: {
        opacityFrom: 0.55,
        opacityTo: 0,
      },
    },
    markers: {
      size: 0,
      strokeColors: "#fff",
      strokeWidth: 2,
      hover: {
        size: 6,
      },
    },
    grid: {
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const month = monthNames[dataPointIndex];
        const wonValue = series[0][dataPointIndex];
        const lostValue = series[1][dataPointIndex];
        const wonAmount = amounts.won[dataPointIndex];
        const lostAmount = amounts.lost[dataPointIndex];

        return `
          <div class="apexcharts-tooltip-custom" style="padding: 8px; background: #fff; border: 1px solid #e5e7eb; border-radius: 6px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);">
            <span style="font-weight: 600;">${month}</span>
            <span style="margin-left: 8px; font-weight: 600; color: #22C55E;">Close Won: ${wonValue} (₹${wonAmount.toFixed(2)})</span>
            <span style="margin-left: 8px; font-weight: 600; color: #EF4444;"> | Close Lost: ${lostValue} (₹${lostAmount.toFixed(2)})</span>
          </div>
        `;
      },
    },
    xaxis: {
      type: "category",
      categories: monthNames,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: "12px",
          colors: ["#6B7280"],
        },
      },
      title: {
        text: "",
        style: {
          fontSize: "0px",
        },
      },
    },
  };

  if (error) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white px-5 pb-5 pt-5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6 sm:pt-6">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-5 pb-5 pt-5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6 sm:pt-6">
      <div className="flex flex-col gap-5 mb-6 sm:flex-row sm:justify-between">
        <div className="w-full">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Opportunity Close Status
          </h3>
          <p className="mt-1 text-gray-500 text-theme-sm dark:text-gray-400">
            Opportunities won and lost each month
          </p>
        </div>
        <div className="flex items-start w-full gap-3 sm:justify-end">
          <div className="relative inline-block">
            <button
              onClick={() => navigate("/opportunity-list")}
              className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 px-3 py-1 rounded-md text-sm font-medium"
            >
              View More
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-full overflow-x-auto custom-scrollbar">
        <div className="min-w-[1000px] xl:min-w-full">
          <Chart options={options} series={series} type="area" height={310} />
        </div>
      </div>
    </div>
  );
}